'use client';

import { useCallback, useEffect, useRef } from 'react';
import { AudioManager } from '@/app/utils/AudioManager';
import { isMobileBrowser } from '@/lib/client-utils';
import { usePathname } from 'next/navigation';

export function AudioUnlocker() {
  const pathname = usePathname();
  const hasInitialized = useRef(false);
  const previousAudioMutedMap = useRef<WeakMap<HTMLAudioElement, boolean>>(new WeakMap());

  const initializeAudio = useCallback(async () => {
    console.log('initializeAudio');
    if (hasInitialized.current) return;

    hasInitialized.current = true;
    console.log('init audio');
    await AudioManager.init();
  }, []);

  useEffect(() => {
    if (pathname?.startsWith('/tv')) {
      void initializeAudio();
    }
  }, [pathname, initializeAudio]);

  useEffect(() => {
    // List of events that indicate user interaction
    const userInteractionEvents = ['click', 'touchstart', 'touchend', 'keydown', 'pointerdown'];

    const handleUserInteraction = () => {
      void initializeAudio();

      // Remove all listeners after first interaction
      userInteractionEvents.forEach((event) => {
        document.removeEventListener(event, handleUserInteraction, { capture: true });
      });
    };

    // Add listeners for user interaction events
    userInteractionEvents.forEach((event) => {
      document.addEventListener(event, handleUserInteraction, {
        capture: true,
        once: false, // We'll manually remove them
      });
    });

    const muteAllHtmlAudio = () => {
      const audioEls = document.querySelectorAll('audio');
      audioEls.forEach((el) => {
        previousAudioMutedMap.current.set(el, el.muted);
        el.muted = true;
      });
    };

    const restoreAllHtmlAudio = () => {
      const audioEls = document.querySelectorAll('audio');
      audioEls.forEach((el) => {
        const prev = previousAudioMutedMap.current.get(el);
        if (typeof prev === 'boolean') {
          el.muted = prev;
        }
      });
    };

    const handleVisibilityChange = async () => {
      if (!isMobileBrowser()) return;
      if (document.hidden) {
        muteAllHtmlAudio();
        await AudioManager.setBackgroundMuted(true);
      } else {
        restoreAllHtmlAudio();
        await AudioManager.setBackgroundMuted(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange, { capture: true });

    // Cleanup function
    return () => {
      userInteractionEvents.forEach((event) => {
        document.removeEventListener(event, handleUserInteraction, { capture: true });
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange, { capture: true });
    };
  }, [initializeAudio]);

  // This component doesn't render anything
  return null;
}
